import {
  createTileLayerComponent,
  updateGridLayer,
  withPane,
} from "@react-leaflet/core";
import axios from "axios";
import { TileLayer, Class, Coords, DoneCallback, TileEvent } from "leaflet";
import { WMSTileLayerProps } from "react-leaflet";
import { Observable } from "rxjs";

type AbortStatus = "init" | "abort";

interface WMSHeaderTileLayerProps extends WMSTileLayerProps {
  headers?: {};
  abort?: Observable<AbortStatus>;
  results?: Observable<string>;
}

function fetchImage(url: string, callback: (blob: Blob) => void, headers: {}) {
  const controller = new AbortController();
  const signal = controller.signal;
  const _axios = axios.create(); //private instance so our Authorization headers are not sent by default!
  try {
    _axios
      .get(url, {
        method: "GET",
        responseType: "blob",
        //@ts-ignore
        mode: "no-cors",
        headers: {
          "content-type": "image/png",
          accept:
            "image/avif,image/webp,image/apng,image/svg+xml,image/*,*/*;q=0.8",
          ...headers,
        },
        signal: signal,
      })
      .then((r) => {
        if (r.data) {
          callback(r.data);
        } else {
          //@ts-ignore
          r.blob()
            .then(callback)
            .catch((error: Error) => {
              if (process.env.NODE_ENV === "development") {
                console.error(error);
              }
              return false;
            });
        }
      })
      .catch((error) => {
        /** //ignore */
        return false;
      });
  } catch (error) {
    //ignore error plix!!
  }
  return controller;
}

type WMSHeaderTileLayerClassProps = {
  new (
    url: string,
    options: Omit<WMSHeaderTileLayerProps, "url">
  ): TileLayer.WMS;
} & typeof Class;

const WMSHeaderTileLayerClass: WMSHeaderTileLayerClassProps =
  TileLayer.WMS.extend({
    initialize: function (url: string, options: WMSHeaderTileLayerProps) {
      const { headers, abort, results, ...props } = options;
      //@ts-ignore : Property 'initialize' does not exist on type 'WMS'.ts(2339)
      TileLayer.WMS.prototype.initialize.call(this, url, props);
      this.headers = headers;
      this.abortControllers = {};
      this.results = results;
      if (this.observer) this.observer.disconnect();
      this.observer = undefined;

      this.on("tileunload", this.tileunload.bind(this));
      this.on("add", this.onAdded.bind(this));
      this.on("remove", this.onRemoved.bind(this));
    },
    createTile(coords: Coords, done: DoneCallback) {
      const url = this.getTileUrl(coords);
      const canvas = document.createElement("canvas");
      canvas.setAttribute("role", "presentation");
      canvas.classList.add("leaflet-tile");
      canvas.setAttribute("width", "256px");
      canvas.setAttribute("height", "256px");
      canvas.setAttribute("data-image-loaded", "false");
      canvas.setAttribute("data-image-url", url);

      this.abortControllers[url] = fetchImage(
        url,
        (blob) => {
          this.fetchImageResult(blob, canvas, done);
        },
        this.headers
      );
      return canvas;
    },
    tileunload(event: TileEvent) {
      const isLoaded = event.tile.getAttribute("data-image-loaded") === "true";
      const url = event.tile.getAttribute("data-image-url") || "impossibru";
      const controller = this.abortControllers[url];
      if (!isLoaded && controller) {
        controller.abort();
        this.abortControllers[url] = null;
        event.tile.setAttribute("data-image-loaded", "false");
      }
    },
    fetchImageResult(
      imageBlob: Blob,
      canvas: HTMLCanvasElement,
      done: DoneCallback | undefined
    ) {
      const thisArg = this;
      try {
        createImageBitmap(imageBlob, 0, 0, 256, 256)
          .then((image) => {
            const ctx = canvas.getContext("2d");
            ctx?.drawImage(image, 0, 0, 256, 256);
            canvas.setAttribute("data-image-loaded", "true");
            const url = canvas.getAttribute("data-image-url") || "impossibru";
            thisArg.abortControllers[url] = null;
            if (done !== undefined) done(undefined, canvas);
            image.close();
          })
          .catch((error) => {
            canvas.setAttribute("data-image-loaded", "false");
            canvas.style.display = "none";
            if (done !== undefined)
              done(new Error("Image load failed"), canvas);
            return false;
          });
      } catch (error) {
        canvas.setAttribute("data-image-loaded", "false");
        canvas.style.display = "none";
        if (done !== undefined) done(new Error("Image load failed"), canvas);
      }
    },
    onAdded() {
      const thisArg = this;
      if (this.observer) this.observer.disconnect();
      this.observer = new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
          mutation.removedNodes.forEach(function (removedCanvas) {
            thisArg.tileunload.call(thisArg, {
              tile: removedCanvas,
              type: "mutationobserved",
            });
          });
        }, thisArg);
      });

      this.observer.observe(this.getContainer(), {
        subtree: true,
        childList: true,
      });
    },
    onRemoved() {
      if (this.observer) this.observer.disconnect();
      const keys = Object.keys(this.abortControllers);
      keys.forEach((key) => {
        const controller = this.abortControllers[key];
        if (controller) controller.abort();
        this.abortControllers[key] = null;
      }, this);
      this.observer = null;
    },
  });

export const WMSHeaderTileLayer = createTileLayerComponent<
  TileLayer.WMS,
  WMSHeaderTileLayerProps
>(
  function createWMSTileLayer({ params = {}, url, ...options }, context) {
    return {
      instance: new WMSHeaderTileLayerClass(url, {
        ...params,
        ...withPane(options, context),
      }),
      context,
    };
  },
  function updateWMSTileLayer(layer, props, prevProps) {
    updateGridLayer(layer, props, prevProps);

    if (props.params != null && props.params !== prevProps.params) {
      layer.setParams(props.params);
    }
  }
);
